import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { getAnalyticsTimeframeFilter } from 'router/subrouters/DataRoom/utils';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const Q_AND_A_CHART_QUERY_KEY = 'data-room-q-and-a-chart';

type UseQAndAChartQueryProps = {
  dataRoomId: string;
};

export default function useQAndAChartQuery({
  dataRoomId,
}: UseQAndAChartQueryProps) {
  const { preferredAnalyticsTimeframe } = useContext(DataRoomContext);

  const getMixpanelProps = useGetMixpanelProps();

  const query = useQuery({
    queryKey: [
      Q_AND_A_CHART_QUERY_KEY,
      { dataRoomId, preferredAnalyticsTimeframe },
    ],
    queryFn: async () => {
      const params = getAnalyticsTimeframeFilter(preferredAnalyticsTimeframe);

      const [{ data: stageData }, { data: subjectData }] = await Promise.all([
        dataRoomApi.questionsAndAnswers.getAnaliticsByStage(dataRoomId, params),
        dataRoomApi.questionsAndAnswers.getAnaliticsBySubject(
          dataRoomId,
          params,
        ),
      ]);

      return { stageData, subjectData };
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!dataRoomId,
  });

  useInvalidateQueryOnUnmount(Q_AND_A_CHART_QUERY_KEY);

  return query;
}
